import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { EpTableComponent, EPTableColumns, EPTableConfig } from '@ep/shared';
import { ActivatedRoute } from '@angular/router';
import { AsyncPipe } from '@angular/common';
import { BehaviorSubject, catchError, of, Subscription } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { Order, OrderTableDisplay } from '@shared/models';
import { EnrollmentTypes, OrderStatusTypes } from '@shared/enums';
import { OrdersApiService } from '@app/areas/merchants/services/orders.api.service';
import { EmployeesApiService } from '@app/areas/merchants/services/employees.api.service';

function hasFlag(value: number, flag: number): boolean {
  return (value & flag) === flag;
}

function mapOrderStatusToValue(order: Order): string {
  if (hasFlag(order.StatusType, OrderStatusTypes.Voided)) return 'Voided';
  if (hasFlag(order.StatusType, OrderStatusTypes.Canceled)) return 'Cancelled';
  if (hasFlag(order.StatusType, OrderStatusTypes.Completed)) return 'Completed';
  if (hasFlag(order.StatusType, OrderStatusTypes.Open)) return 'Open';
  return 'Pending';
}

function mapOrderEnrollmentToValue(order: Order): string {
  if (hasFlag(order.EnrollmentType, EnrollmentTypes.CanceledEnrollment)) return 'Cancelled';
  if (hasFlag(order.EnrollmentType, EnrollmentTypes.LoyaltyProgramDeclined)) return 'Declined';
  if (hasFlag(order.EnrollmentType, EnrollmentTypes.LoyaltyProgramExisting)) return 'Existing';
  if (hasFlag(order.EnrollmentType, EnrollmentTypes.LoyaltyProgramEnrolled)) return 'Enrolled';
  return 'N/A';
}

function mapOrderReviewStatusToValue(display: OrderTableDisplay): string {
  if (display.Score === undefined) return '';
  if (display.Score === null) return 'Not Answered';
  if (display.Score === 0 || display.Score === 1) return 'Poor';
  if (display.Score === 5) return 'Great';
  return 'Mixed';
}

function getOrderReviewStatusSeverity(display: OrderTableDisplay): string {
  if (display.Score === undefined) return 'empty';
  if (display.Score === null) return 'primary';
  if (display.Score === 0 || display.Score === 1) return 'danger';
  if (display.Score === 5) return 'success';
  return 'warning';
}

@Component({
  selector: 'app-order-table',
  templateUrl: './order-table.component.html',
  styleUrls: ['./order-table.component.scss'],
  standalone: true,
  imports: [EpTableComponent, AsyncPipe],
})
export class OrderTableComponent implements OnInit, OnDestroy {
  private readonly ordersApiService = inject(OrdersApiService);
  private readonly employeesApiService = inject(EmployeesApiService);
  private readonly route = inject(ActivatedRoute);

  private merchantId = 0;
  private employeeNamesById = new Map<number, string>();
  private lastPageNumber = 1;
  private lastPageSize = 10;
  private lastSearch: string | null = null;
  private searchDebounce: ReturnType<typeof setTimeout> | undefined;
  private subs = new Subscription();

  protected config!: EPTableConfig<OrderTableDisplay>;
  protected totalRecords = 0;
  protected orders$ = new BehaviorSubject<OrderTableDisplay[] | null>(null);

  ngOnInit(): void {
    this.initializeTableConfig('normal');

    this.subs.add(
      this.route
        .parent!.paramMap.pipe(
          map((params) => Number(params.get('merchantId'))),
          switchMap((merchantId) => {
            this.merchantId = merchantId;
            return this.employeesApiService.getByMerchantId(merchantId);
          })
        )
        .subscribe((employees) => {
          this.employeeNamesById = new Map(
            employees.map((employee) => [employee.EmployeeId, `${employee.NameFirst} ${employee.NameLast}`])
          );
          this.fetchOrders(this.lastPageNumber, this.lastPageSize);
        })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    clearTimeout(this.searchDebounce);
  }

  protected pageChangeEvent(event: { first: number; rows: number }): void {
    this.lastPageNumber = event.first === 0 ? 1 : Math.floor(event.first / event.rows) + 1;
    this.lastPageSize = event.rows;
    this.fetchOrders(this.lastPageNumber, this.lastPageSize, this.lastSearch ?? undefined);
  }

  protected debounceSearch(search: string): void {
    const normalizedSearch = search === '' ? null : search;
    if (normalizedSearch === this.lastSearch) {
      return;
    }

    clearTimeout(this.searchDebounce);
    this.searchDebounce = setTimeout(() => {
      this.lastSearch = normalizedSearch;
      this.lastPageNumber = 1;
      this.fetchOrders(1, this.lastPageSize, this.lastSearch ?? undefined);
    }, 300);
  }

  private fetchOrders(pageNumber: number, pageSize: number, query?: string): void {
    this.subs.add(
      this.ordersApiService
        .getByMerchantId(this.merchantId, pageNumber, pageSize, query)
        .pipe(catchError(() => of({ orders: [], totalTransactions: 0 })))
        .subscribe(({ orders, totalTransactions }) => {
          this.totalRecords = totalTransactions;
          this.validateColumns(orders);
          this.orders$.next(orders.map((order) => this.mapToDisplay(order)));
        })
    );
  }

  private mapToDisplay(order: Order): OrderTableDisplay {
    const employeeName = order.EmployeeId ? this.employeeNamesById.get(order.EmployeeId) : undefined;
    return {
      OrderId: order.OrderId,
      Employee: employeeName ?? '',
      StatusType: mapOrderStatusToValue(order),
      EnrollmentType: mapOrderEnrollmentToValue(order),
      AmountTotal: order.AmountTotal,
      StartDateTime: order.StartDateTime,
      Score: order.OrderReviews.length > 0 ? order.OrderReviews[0].Score : undefined,
      OrderGuid: order.OriginSDK ? undefined : order.OrderGuid?.slice(-9),
      ReferenceIdent: order.OriginSDK ? order.OrderPayments[0]?.ReferenceIdent : undefined,
      OriginSDK: order.OriginSDK,
    };
  }

  private validateColumns(orders: Order[]): void {
    if (!orders.length) {
      this.initializeTableConfig('normal');
      return;
    }

    const hasOriginSDK = orders.some((order) => order.OriginSDK === true);
    const hasNormalOrders = orders.some((order) => order.OriginSDK !== true);

    if (hasOriginSDK && hasNormalOrders) {
      this.initializeTableConfig('allColumns');
    } else if (hasOriginSDK) {
      this.initializeTableConfig('referenceIdent');
    } else {
      this.initializeTableConfig('normal');
    }
  }

  private initializeTableConfig(columnConfig: 'normal' | 'referenceIdent' | 'allColumns'): void {
    const baseColumns: EPTableColumns<OrderTableDisplay>[] = [
      {
        header: 'Date',
        field: 'StartDateTime',
        columnType: 'date',
        dateFormat: 'MM/dd/YY h:mm a',
        defaultValue: null,
        disableSorting: true,
      },
    ];

    const orderIdColumn: EPTableColumns<OrderTableDisplay> = {
      header: 'Order Id',
      field: 'OrderGuid',
      columnType: 'string',
      defaultValue: '',
      disableSorting: true,
    };

    const paymentIdColumn: EPTableColumns<OrderTableDisplay> = {
      header: 'Payment Id',
      field: 'ReferenceIdent',
      columnType: 'string',
      defaultValue: '',
      disableSorting: true,
    };

    const commonColumns: EPTableColumns<OrderTableDisplay>[] = [
      {
        header: 'Employee',
        field: 'Employee',
        columnType: 'string',
        defaultValue: 'Missing employee',
        disableSorting: true,
      },
      {
        header: 'Status',
        field: 'StatusType',
        columnType: 'string',
        defaultValue: 'Open',
        disableSorting: true,
      },
      {
        header: 'Enrollment',
        field: 'EnrollmentType',
        columnType: 'string',
        defaultValue: 'Declined',
        disableSorting: true,
      },
      {
        header: 'Total',
        field: 'AmountTotal',
        columnType: 'price',
        defaultValue: 0,
        disableSorting: true,
      },
      {
        header: 'Rating',
        field: 'Score',
        columnType: 'categorical',
        defaultValue: 'Poor',
        disableSorting: true,
        fieldToValueMap: mapOrderReviewStatusToValue,
        severity: getOrderReviewStatusSeverity,
      },
    ];

    let columns: EPTableColumns<OrderTableDisplay>[];
    switch (columnConfig) {
      case 'referenceIdent':
        columns = [...baseColumns, paymentIdColumn, ...commonColumns];
        break;
      case 'allColumns':
        columns = [...baseColumns, orderIdColumn, paymentIdColumn, ...commonColumns];
        break;
      default:
        columns = [...baseColumns, orderIdColumn, ...commonColumns];
        break;
    }

    this.config = {
      header: 'transactions',
      lazy: true,
      pagination: {
        rows: 10,
        showCurrentPageReport: true,
      },
      columns,
      style: {
        'min-width': '50rem',
        border: '5px',
      },
      globalFilterFields: ['Employee', 'OrderGuid', 'AmountTotal'],
    };
  }
}
